/* WorkTrigHistoryViewComponent.js */

"use strict";
const _ = require("lodash");
const EventEmitter = require("events").EventEmitter;
//const UserGridData = require('@gluenetworks/entity_grid_gl').UserData;

const Renderer = require("./WorkTrigHistoryViewRenderer");
const DateColumn = require("./column_renderers/date_column/DateComponent");
const ElapsedColumn = require("./column_renderers/elapsed_column/ElapsedComponent");
const ExecutionTypeColumn = require("./column_renderers/execution_type/ExecutionTypeComponent");
const Registrations = require("../../../../../RegistrationNamespaces");
const ScheduleStatusColumn = require("./column_renderers/schedule_status/ScheduleStatusComponent");
const LinksColumn = require("./column_renderers/links/LinksComponent");
const PagingWorkTrigHistoryFeed = require("./PagingWorkTrigHistoryFeed");

class WorkTrigHistoryViewComponent extends EventEmitter {
    constructor(
        logger,
        gameEditor,
        userSession,
        storageFactory,
        steveIo,
        reportService,
        solutionsManager,
        MUF
    ) {
        super();
        this._logger = logger;
        this._gameEditor = gameEditor;
        this._userSession = userSession;
        this._steveIo = steveIo;
        this._reportService = reportService;
        this._solutionName =
            (solutionsManager.activeSolution && solutionsManager.activeSolution.name) || "";
        this._gridKey = _.camelCase(this._solutionName) + "_" + "futureView";
        this._userGridData = storageFactory.getRemoteStorageInstanceForEntityGrid(
            "entity_grid_gl",
            this._gridKey
        );
        this._explorerState = {
            selectedIds: [],
        };

        this._entity = undefined;
        this._pagingFeed = undefined;
        this._modalUserFeedback = MUF;

        this._renderer = new Renderer(this);
    }

    get columnConfig() {
        let columns = {
            expectedExecutionTime: {
                label: "Scheduled Start",
                columnRendererComponent: DateColumn,
                width: 170,
                priority: 1,
            },
            actualExecutionTime: {
                label: "Actual Start",
                columnRendererComponent: DateColumn,
                width: 170,
                priority: 1,
            },
            launchedBy: {
                label: "Execution Type",
                columnRendererComponent: ExecutionTypeColumn,
                width: 120,
                priority: 2,
            },
            targetCount: {
                label: "Targets",
                width: 75,
                priority: 3,
            },
            status: {
                label: "Run Status",
                entityColumn: true,
                columnRendererComponent: ScheduleStatusColumn,
                width: 120,
                priority: 4,
            },
            successCount: {
                label: "Successes",
                width: 75,
                priority: 5,
            },
            failureCount: {
                label: "Failures",
                width: 75,
                priority: 6,
            },
            elapsed: {
                label: "Elapsed Time",
                columnRendererComponent: ElapsedColumn,
                width: 100,
                priority: 4,
            },
            links: {
                label: "",
                width: 80,
                columnRendererComponent: LinksColumn,
                context: {
                    gameEditor: this._gameEditor,
                    triggerType: this._entity,
                    logger: this._logger,
                    userSession: this._userSession,
                    MUF: this._modalUserFeedback,
                },
                entityColumn: true,
                sortable: false,
                priority: 7,
            },
        };

        /*
        launchedBy: {
            label: 'Launched By',
                entityColumn: 'launchedBy',
                width: 100,
                priority: 2
        },
        expectedExecutionTime: {
            label: 'Expected Execution Time',
                entityColumn: 'expectedExecutionTime',
                columnRendererComponent: DateColumn,
                width: 175,
                priority: 3
        },
        actualExecutionTime: {
            label: 'Actual Execution Time',
                entityColumn: 'actualExecutionTime',
                columnRendererComponent: DateColumn,
                width: 175,
                priority: 4
        },
        completionTime: {
            label: 'Completion Time',
                entityColumn: 'completionTime',
                columnRendererComponent: DateColumn,
                width: 175,
                priority: 5
        },
        errorMsg: {
            label: 'Failure Cause',
            entityColumn: 'errorMsg',
            width: 200,
            priority: 9
        }
        */

        return columns;
    }

    get pagingFeed() {
        return this._pagingFeed;
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS   */
    /*******************************************/

    _registerListeners() {}

    _unregisterListeners() {}

    _patchSorting(sorting) {
        let patchedSorting = _.clone(sorting);
        // add second sorting order by execution time
        if (sorting && Object.keys(sorting).length === 1 && !sorting.expectedExecutionTime) {
            patchedSorting.expectedExecutionTime = 1;
        }
        return patchedSorting;
    }

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS     */
    /* START PUBLIC COMPONENT MODEL METHODS    */
    /*******************************************/

    whenModelIsReady() {
        return this._gameEditor.whenTriggerTypeIsReady().then(() => {
            const userSorting = this._userGridData.getSorting() || {};
            const patchedSorting = this._patchSorting(userSorting);
            this._entity = this._gameEditor.getTriggerType();
            this._pagingFeed = new PagingWorkTrigHistoryFeed({
                feedKey: this._gridKey + "_feed",
                userSession: this._userSession,
                entityDef: this._gameEditor.getTriggerType(),
                columnConfig: this.columnConfig,
                sort: patchedSorting,
                additionalDataFields: ["statusChangeTime", "errorMsg", "workId"],
            });
            this._registerListeners();
            return this.whenFeedReady();
        });
    }

    wasFeedPreviouslyInitialized() {
        return this._pagingFeed.wasPreviouslyInitialized();
    }

    whenFeedReady() {
        return this._pagingFeed.whenFeedReady();
    }

    get renderer() {
        return this._renderer;
    }

    get gameEditor() {
        return this._gameEditor;
    }

    get triggerType() {
        return this._entity;
    }

    get logger() {
        return this._logger;
    }

    saveUserSettingsToStorage() {
        this._saveUserSettingsToStorage();
    }

    retrieveTriggerWork(triggerId) {
        let trigger = this._pagingFeed.getEntityById(triggerId);
        if (!trigger) {
            return Promise.resolve();
        }
        return trigger.retrieveWork();
    }

    shutdown() {
        this._unregisterListeners();
        let shutdowns = [this._renderer.shutdown(), this._pagingFeed.shutdown()];
        return Promise.all(shutdowns).then(
            () => {
                this._logger = undefined;
                this._gameEditor = undefined;
                this._userSession = undefined;
                this._renderer = undefined;
                this._explorerState = undefined;
                this._pagingFeed = undefined;
                this._gridKey = undefined;
                this._userGridData = undefined;
                this._entity = undefined;
            },
            (err) => {
                this._logger.warn({error: err}, "WorkTrigHistoryViewComponent.shutdown() error");
            }
        );
    }

    get explorerState() {
        return this._explorerState;
    }

    set explorerState(val) {
        this.setExplorerState(val);
    }

    setExplorerState(val) {
        this._explorerState = val;
    }

    updateExplorerState(property, val) {
        _.set(this._explorerState, property, val);
    }

    setSort(sortData) {
        let sort = {};
        sort[sortData.sortColumn] = sortData.sort === "asc" ? 1 : -1;
        this._patchSorting(sort);
        this._pagingFeed.setSort(sort);
    }

    resetSelection() {
        let selectedIds = this.getSelectedTriggerTypeIds().selectedRows;
        if (!selectedIds || !selectedIds.length) {
            return;
        }

        selectedIds.splice(0, selectedIds.length);
        this.updateExplorerState("selectedIds", {selectedRows: selectedIds});
    }

    getViewSettings() {
        if (this._routeData.downloadWorkResults) {
            return {
                view: Registrations.WTE_VIEW_HISTORY,
                action: "resultReport",
                triggerTypeId: this._routeData.downloadWorkResults.triggerTypeId,
                triggerId: this._routeData.downloadWorkResults.triggerId,
            };
        }
    }

    /*******************************************/
    /* END COMPONENT MODEL METHODS             */
    /* START HANDLER METHODS                   */

    /*******************************************/

    _onUserSorted(sortData) {
        let sort = {};
        sort[this.columnConfig[sortData.sortColumn].path || sortData.sortColumn] =
            sortData.sort === "asc" ? 1 : -1;
        this._patchSorting(sort);

        this._pagingFeed.setSort(sort);
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PUBLIC API METHODS                */
    /*******************************************/

    getSelectedTriggerTypeIds() {
        if (!this._explorerState || !this._explorerState.selectedIds) {
            return [];
        }
        return this._explorerState.selectedIds;
    }

    downloadTriggerReport() {
        return this._entity
            .runHistoryReportWork()
            .then((report) => {
                let fileName = `History-Report-${_.replace(
                    this._entity.displayName,
                    /\s/g,
                    "-"
                )}-${new Date().toISOString()}.csv`;
                let history = report.contents;
                let file = new this._steveIo.File(fileName, null, history);
                this._steveIo.fileFactory.saveFile(file);
                return Promise.resolve();
            })
            .catch((err) => {
                this._logger.warn({error: err}, "downloadTriggerReport() - Error");
            });
    }

    downloadWorkReport(triggerEntity) {
        return triggerEntity
            .generateWorkReports()
            .then((reports) => {
                if (_.isEmpty(reports)) {
                    throw new Error("Cannot retrieve the selected workEntity report");
                }
                let report = Object.values(reports)[0];
                let fileName = `Work-Result-Report-${_.replace(
                    this._entity.displayName,
                    /\s/g,
                    "-"
                )}-${new Date().toISOString()}.csv`;
                let file = new this._steveIo.File(fileName, null, report.contents);
                this._steveIo.fileFactory.saveFile(file);
                return Promise.resolve();
            })
            .catch((err) => {
                this._logger.warn({error: err}, "downloadWorkReport() - Error");
            });
    }

    /*******************************************/
    /* END PUBLIC API METHODS                  */
    /*******************************************/
}

module.exports = WorkTrigHistoryViewComponent;

WorkTrigHistoryViewComponent.$inject = [
    "$teve.logger",
    "gameEditor.gameEditor",
    "$teve.userSession",
    "$teve.storageFactory",
    "$teve.io",
    "$teve.services.instance.Report",
    "$teve.solutionsManager",
    "$teve.ui.userFeedback",
];

WorkTrigHistoryViewComponent.$displayDetails = {
    type: "View",
    name: "Schedule History",
    icon: "worktrigger.history",
    shown: true,
    title: "Schedule History",
    order: 2,
};
